import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GradientButton } from './ui/gradient-button';

function Header() {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      console.error('Video ref is null');
      return;
    }

    // Hide video initially to prevent showing poster or cached images
    video.style.opacity = '0';
    video.style.visibility = 'hidden';

    // Ensure video loops infinitely - set both properties and attributes for mobile compatibility
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('loop', '');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', ''); // iOS Safari
    video.setAttribute('x5-playsinline', ''); // Android X5 browser

    let isPlaying = false; // Track if video is currently playing
    let playPromise = null; // Track the play promise to avoid multiple simultaneous attempts

    // Detect if we're on mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                     (window.innerWidth <= 768);

    // Function to show video when ready
    const showVideo = () => {
      if (video && video.readyState >= 2) {
        video.style.opacity = '1';
        video.style.visibility = 'visible';
        video.style.transition = 'opacity 0.3s ease-in-out';
        setVideoReady(true);
      }
    };

    // Function to attempt playing the video
    const attemptPlay = async () => {
      // If already playing, don't try again
      if (isPlaying && !video.paused) return;
      
      // If there's already a play attempt in progress, don't start another
      if (playPromise) return;

      try {
        // On mobile, wait for more data (readyState >= 3) for better reliability
        const minReadyState = isMobile ? 3 : 2;
        
        if (video.readyState >= minReadyState) {
          // Show video before playing
          showVideo();
          playPromise = video.play();
          await playPromise;
          isPlaying = true;
          playPromise = null;
          console.log('✅ Video is now playing');
        } else {
          console.log('⏳ Video not ready yet, readyState:', video.readyState, 'need:', minReadyState);
          // On mobile, wait for more data and try again
          if (isMobile && video.readyState < 3) {
            video.addEventListener('canplaythrough', () => {
              if (!isPlaying) {
                showVideo();
                attemptPlay();
              }
            }, { once: true });
          }
        }
      } catch (error) {
        playPromise = null;
        console.log('⚠️ Play attempt failed:', error.name);
        // On mobile, we need user interaction - don't give up
        if (isMobile) {
          console.log('💡 Waiting for user interaction on mobile...');
        }
      }
    };

    // Error handler
    const handleError = () => {
      console.error('❌ Video error:', video.error);
      if (video.error) {
        console.error('Error code:', video.error.code);
        console.error('Error message:', video.error.message);
      }
      // Show video even on error (will show poster if available)
      showVideo();
    };

    // Ensure video loops when it ends
    const handleEnded = () => {
      video.currentTime = 0;
      video.play().catch(() => {
        // Ignore play errors on loop restart
      });
    };

    // Play when video can play
    const handleCanPlay = () => {
      showVideo();
      if (!isPlaying) {
        attemptPlay();
      }
    };

    // Handle when video starts playing
    const handlePlaying = () => {
      isPlaying = true;
      showVideo();
    };

    // Add event listeners
    video.addEventListener('canplay', handleCanPlay, { once: true });
    video.addEventListener('canplaythrough', handleCanPlay, { once: true });
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    
    // Persistent interaction handlers for mobile (not once: true)
    // These need to stay active to catch any user interaction
    const handleInteraction = (e) => {
      // On mobile, be more aggressive - try to play on any touch
      if (isMobile || video.paused) {
        if (!isPlaying) {
          showVideo();
          attemptPlay();
        }
      }
    };

    // Mobile-specific events - keep these active (not once: true)
    // Use capture phase for better mobile detection
    const mobileEvents = ['touchstart', 'touchend', 'click'];
    mobileEvents.forEach(eventType => {
      // Add to document with capture phase
      document.addEventListener(eventType, handleInteraction, { passive: true, capture: true });
      // Also add to video element directly
      video.addEventListener(eventType, handleInteraction, { passive: true });
    });

    // Also listen on the header and body for better mobile capture
    const header = document.querySelector('header');
    const body = document.body;
    [header, body].forEach(element => {
      if (element) {
        mobileEvents.forEach(eventType => {
          element.addEventListener(eventType, handleInteraction, { passive: true });
        });
      }
    });

    // Try to play immediately if video is already loaded
    if (video.readyState >= 3) { // HAVE_FUTURE_DATA or HAVE_ENOUGH_DATA
      showVideo();
      attemptPlay();
    } else if (video.readyState >= 2) { // HAVE_CURRENT_DATA
      showVideo();
      attemptPlay();
    } else {
      // Wait for video metadata
      const handleLoadedMetadata = () => {
        showVideo();
        attemptPlay();
      };
      video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    }

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('canplaythrough', handleCanPlay);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('loadedmetadata', attemptPlay);
      mobileEvents.forEach(eventType => {
        document.removeEventListener(eventType, handleInteraction, { capture: true });
        video.removeEventListener(eventType, handleInteraction);
        [header, body].forEach(element => {
          if (element) {
            element.removeEventListener(eventType, handleInteraction);
          }
        });
      });
    };
  }, []);

  return (
    <header 
      style={{ 
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: '#1a1a1a' // Dark background to prevent flash while video loads
      }} 
      role="banner" 
      id="home"
    >
      <video 
        ref={videoRef} 
        src="/video.mp4" 
        loop
        autoPlay
        muted
        playsInline
        preload="auto"
        aria-label="Background video of solar panels"
        onError={(e) => {
          console.error('Video element error:', e);
          console.error('Video src:', e.target.src);
        }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          display: 'block',
          visibility: videoReady ? 'visible' : 'hidden',
          opacity: videoReady ? 1 : 0,
          backgroundColor: 'transparent',
          pointerEvents: 'auto', // Allow touch events on mobile
          filter: 'brightness(0.6)',
          transition: 'opacity 0.3s ease-in-out'
        }}
      />
      <h1>Lunar Electric - Your Trusted Solar Energy Partner in India</h1>
      <p className="sr-only">Professional rooftop solar panel installation services with storm-proof and rust-proof structures for residential, commercial, and utility-scale projects across India. Get transparent pricing, government subsidies, and 1-year free maintenance.</p>
      <div className="header-cta" role="region" aria-label="Call to action buttons">
        <GradientButton asChild>
          <Link to="/callback" aria-label="Go solar">
            Go Solar
          </Link>
        </GradientButton>
        <a href="tel:+919148857417" className="btn btn-secondary" aria-label="Call us now">
          Call Now
        </a>
      </div>
      <div className="row"></div>
      <div className="headerbg"></div>
    </header>
  );
}

export default Header;

